import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatLocalDate } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const AMENITIES = [
  { key: "clubhouse", label: "Clubhouse" },
  { key: "pool", label: "Pool" },
  { key: "tennis", label: "Tennis Court" },
];
const SLOTS = ["8:00 – 10:00 AM", "10:00 – 12:00 PM", "12:00 – 2:00 PM", "2:00 – 4:00 PM", "4:00 – 6:00 PM", "6:00 – 8:00 PM"];

export default function BookingsPage() {
  const [amenity, setAmenity] = useState("clubhouse");
  const [date, setDate] = useState(new Date());
  const [slot, setSlot] = useState("");
  const [guests, setGuests] = useState(1);
  const [notes, setNotes] = useState("");
  const [booked, setBooked] = useState([]);
  const [my, setMy] = useState([]);

  const dateStr = date.toISOString().split("T")[0];

  const loadAvailability = async () => {
    try {
      const { data } = await api.get(`/bookings/availability`, { params: { amenity, date: dateStr } });
      setBooked(data.booked_slots || []);
    } catch (e) { console.error("Availability load failed:", e); }
  };

  const loadMy = async () => {
    try {
      const { data } = await api.get("/bookings/my");
      setMy(data);
    } catch (e) { console.error("My bookings load failed:", e); }
  };

  useEffect(() => { loadAvailability(); }, [amenity, dateStr]);
  useEffect(() => { loadMy(); }, []);

  const handleSubmit = async () => {
    if (!slot) return toast.error("Pick a time slot");
    try {
      await api.post("/bookings", {
        amenity,
        date: dateStr,
        time_slot: slot,
        guests: parseInt(guests, 10),
        notes,
      });
      toast.success("Booking submitted! Check your email.");
      setSlot("");
      setNotes("");
      loadAvailability();
      loadMy();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Booking failed");
    }
  };

  return (
    <div data-testid="bookings-page" className="max-w-6xl mx-auto px-6 py-12">
      <p className="text-xs tracking-[0.3em] uppercase text-[#C86650] mb-2">Portal</p>
      <h1 className="font-serif text-4xl text-[#1A2421] mb-2">Book an Amenity</h1>
      <p className="text-[#5C6A64] mb-10">Reserve up to 30 days in advance.</p>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="rounded-2xl border-[#D5CFC4] lg:col-span-1">
          <CardContent className="p-6">
            <p className="text-sm font-medium text-[#1A2421] mb-3">Select date</p>
            <Calendar
              mode="single"
              selected={date}
              onSelect={(d) => d && setDate(d)}
              disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
              data-testid="booking-calendar"
              className="rounded-xl"
            />
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-[#D5CFC4] lg:col-span-2">
          <CardContent className="p-8">
            <div className="grid sm:grid-cols-2 gap-5 mb-5">
              <div>
                <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Amenity</label>
                <Select value={amenity} onValueChange={setAmenity}>
                  <SelectTrigger data-testid="book-amenity-select" className="mt-2 rounded-xl h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {AMENITIES.map((a) => <SelectItem key={a.key} value={a.key}>{a.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Guests</label>
                <Input data-testid="book-guests" type="number" min="1" max="60" value={guests} onChange={(e) => setGuests(e.target.value)} className="mt-2 rounded-xl h-11" />
              </div>
            </div>

            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Time slot</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2 mb-5">
              {SLOTS.map((s) => {
                const isBooked = booked.includes(s);
                const isSelected = slot === s;
                return (
                  <button
                    key={s}
                    data-testid={`slot-${s.replace(/[^a-z0-9]/gi, "-")}`}
                    disabled={isBooked}
                    onClick={() => setSlot(s)}
                    className={`text-xs py-3 rounded-xl border transition-colors ${
                      isBooked
                        ? "bg-[#E5E1D8] text-[#5C6A64] cursor-not-allowed line-through"
                        : isSelected
                        ? "bg-[#2C4C3B] text-white border-[#2C4C3B]"
                        : "bg-white border-[#D5CFC4] text-[#1A2421] hover:border-[#2C4C3B]"
                    }`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>

            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Notes (optional)</label>
            <Textarea data-testid="book-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="mt-2 rounded-xl" />

            <Button data-testid="book-submit" onClick={handleSubmit} className="mt-6 w-full rounded-full h-12 bg-[#C86650] hover:bg-[#B4553F] text-white">
              Submit booking request
            </Button>
          </CardContent>
        </Card>
      </div>

      <div className="mt-12">
        <h2 className="font-serif text-2xl text-[#1A2421] mb-4">My bookings</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {my.length === 0 && <p className="text-[#5C6A64]">No bookings yet.</p>}
          {my.map((b) => (
            <Card key={b.id} data-testid={`my-booking-${b.id}`} className="rounded-2xl border-[#D5CFC4]">
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-2">
                  <p className="font-serif text-xl text-[#1A2421] capitalize">{b.amenity}</p>
                  <Badge className={
                    b.status === "confirmed" ? "bg-[#2C4C3B]" :
                    b.status === "rejected" || b.status === "cancelled" ? "bg-[#C86650]" :
                    "bg-[#EADCC8] text-[#2C4C3B]"
                  }>
                    {b.status}
                  </Badge>
                </div>
                <p className="text-sm text-[#5C6A64]">{formatLocalDate(b.date, { weekday: "short", month: "short", day: "numeric", year: "numeric" })} · {b.time_slot}</p>
                <p className="text-xs text-[#5C6A64] mt-1">{b.guests} guest(s)</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
